<template>
  <section>
    <h2 class="body-1-bold">
      {{ i18n(translations.filterByStars) }}
    </h2>
    <ul>
      <RevListItemStatic
        v-for="range in rangesList"
        :key="range.label"
        class="-mb-16"
        role="radioGroup"
      >
        <template #label>
          <div
            :class="[
              'flex items-center justify-between',
              {
                'cursor-pointer': !range.disabled,
                'cursor-not-allowed': range.disabled,
              },
            ]"
            role="button"
            tabindex="-1"
            @click.prevent="handleRangeClick(range)"
            @keydown="handleRangeClick(range)"
          >
            <RevRadio
              :id="range.selected"
              class="w-1/4 md:w-1/3"
              :disabled="range.disabled"
              :model-value="selectedRange === range.selected"
              name="rateFilters"
              :value="range.selected"
            >
              <template #label>
                <div class="body-1">{{ range.label }}</div>
              </template>
            </RevRadio>

            <div class="flex w-4/5 items-center justify-between md:w-2/3">
              <ProgressBar
                class="mx-2 grow"
                :current-value="range.value"
                :is-loading="loading"
              />

              <div class="body-1 w-1/3 text-right">
                {{
                  i18n(translations.percentage, {
                    percentage: range.value,
                  })
                }}
              </div>
            </div>
          </div>
        </template>
      </RevListItemStatic>
    </ul>

    <div v-if="merchantId && displayMerchantFilter" class="mt-32">
      <h2 class="body-1-bold mb-8">
        {{ i18n(translations.filterReviewsParametersTitle) }}
      </h2>

      <div class="flex items-center gap-4">
        <RevToggle
          id="filterBySeller"
          v-model="filterReviewsByCurrentSeller"
          :label="i18n(translations.filterReviewsCurrentSeller)"
          @update:model-value="onFilterReviewsByCurrentSellerClick"
        />

        <RevTooltip class="caption z-10" position="top">
          <template #trigger="{ show, hide }">
            <IconInfo
              class="size-16"
              @focusin="show"
              @focusout="hide"
              @mouseenter="show"
              @mouseleave="hide"
            />
          </template>

          <p class="mb-4">
            {{ i18n(translations.filterReviewsCurrentSellerTooltipTitle) }}
          </p>

          <p>{{ i18n(translations.filterReviewsCurrentSellerTooltipText) }}</p>
        </RevTooltip>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { navigateTo, useRoute } from '#imports'
import { watch } from 'vue'

import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevListItemStatic } from '@ds/components/ListItemStatic'
import { RevRadio } from '@ds/components/Radio'
import { RevToggle } from '@ds/components/Toggle'
import { RevTooltip } from '@ds/components/Tooltip'
import { IconInfo } from '@ds/icons/IconInfo'

import { TRACKING_PAGE_TYPE_BY_ROUTE_NAME } from '~/constants/trackingPageType'

import { TRACKING_ZONE } from '../../constants'
import ProgressBar from '../ProgressBar/ProgressBar.vue'

import translations from './Filters.translations'
import { useRatingFilters } from './composables/useRatingFilters'
import { useSellerFilter } from './composables/useSellerFilter'

const props = defineProps<{ merchantId?: string; resetFilters?: number }>()

export type ReviewFilterOption = 'review-rate' | 'review-seller'

const emit = defineEmits<{
  (e: 'active-filters', value: ReviewFilterOption[]): void
}>()

const route = useRoute()
const i18n = useI18n()
const experiments = useExperiments()

const { trackClick } = useTracking()

const { selectedRange, rangesList, loading, resetRatingFilters } =
  useRatingFilters()
const { filterReviewsByCurrentSeller, resetSellerFilter } = useSellerFilter()

type Range = {
  disabled: boolean
  label: string
  selected: string
  value: number
  min: number
  max: number
}

const displayMerchantFilter =
  experiments['experiment.reviewSellerAndFilters'] === 'showSeller'

function emitActiveFilters() {
  const queryParams = route.query

  const activeFilters: ReviewFilterOption[] = []
  for (const [param] of Object.entries(queryParams)) {
    switch (param) {
      case 'seller_public_id':
        activeFilters.push('review-seller')
        break
      case 'global_rate__gte':
      case 'global_rate__lte':
        activeFilters.push('review-rate')
        break
      default:
    }
  }

  emit('active-filters', activeFilters)
}

async function resetFiltersValues() {
  await resetRatingFilters()
  await resetSellerFilter()
}

async function handleRangeClick({ selected, disabled, min, max }: Range) {
  if (!disabled && selectedRange.value !== selected) {
    selectedRange.value = selected

    const routeName = route.name?.toString() || ''

    trackClick({
      pageType: TRACKING_PAGE_TYPE_BY_ROUTE_NAME[routeName],
      zone: TRACKING_ZONE,
      name: `review_star_filter_between_${min}_and_${max}`,
    })

    await navigateTo({
      ...route,
      // I don't know why the spread isn't keeping the hash, but without it we'll lose it, which is problematic on the product page
      // as we heavily rely on it for the current grade / new battery state.
      hash: route.hash,
      query: {
        ...route.query,
        global_rate__gte: min,
        global_rate__lte: max,
      },
    })
  }
}

async function onFilterReviewsByCurrentSellerClick() {
  if (!props.merchantId) return

  const routeName = route.name?.toString() || ''

  trackClick({
    pageType: TRACKING_PAGE_TYPE_BY_ROUTE_NAME[routeName],
    zone: TRACKING_ZONE,
    name: 'review_seller',
  })

  await navigateTo({
    ...route,
    hash: route.hash,
    query: {
      ...route.query,
      ...(filterReviewsByCurrentSeller.value
        ? { seller_public_id: props.merchantId }
        : { seller_public_id: undefined }),
    },
  })
}

watch(
  () => route.query,
  () => emitActiveFilters(),
  { immediate: true },
)

watch(
  () => props.resetFilters,
  () => resetFiltersValues(),
)
</script>
